import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getReporte, getStartups } from '../services/apiService';
import { mergeContenido } from './PreparacionReportes';
import VistaInforme from './VistaInforme';
import { TocIcon } from './IconsReporte';
import '../styles-reporte.css';

const NAV_ITEMS = [
  { id: 'introduccion', label: 'Introducción' },
  { id: 'resumen-ejecutivo', label: 'Resumen Ejecutivo' },
  { id: 'actividad-inversion', label: 'Actividad de inversión' },
  { id: 'eventos', label: 'Ecosistema' },
  { id: 'indicadores', label: 'Indicadores' },
  { id: 'participadas', label: 'Cartera' },
  { id: 'calendario', label: 'Calendario' },
  { id: 'anexos', label: 'Anexos' }
];

export default function InformePublicoPage() {
  const { id } = useParams();
  const [reporte, setReporte] = useState(null);
  const [startupsReporte, setStartupsReporte] = useState([]);
  const [contenidoGenerico, setContenidoGenerico] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const [report, startupsList] = await Promise.all([
          getReporte(parseInt(id, 10)),
          getStartups()
        ]);
        if (cancelled) return;
        if (!report || !report.id) {
          setError('Reporte no encontrado');
          setLoading(false);
          return;
        }
        setReporte(report);
        setContenidoGenerico(mergeContenido(report.contenidoGenerico || {}));
        const ids = report.startupsIds || [];
        const datosStartups = report.datosStartups || {};
        const list = ids.map((sid) => {
          const s = startupsList.find((x) => x.id === sid);
          if (!s) return null;
          return {
            ...s,
            datos: datosStartups[sid] || null
          };
        }).filter(Boolean);
        setStartupsReporte(list);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Error al cargar el informe');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="reporte-pinama min-h-screen flex items-center justify-center">
        <div className="text-center" style={{ color: 'var(--ink-60)' }}>
          <div className="h-10 w-10 border-2 border-[var(--teal)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p>Cargando informe...</p>
        </div>
      </div>
    );
  }

  if (error || !reporte) {
    return (
      <div className="reporte-pinama min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="mb-4" style={{ color: 'var(--ink)' }}>{error || 'Reporte no encontrado'}</p>
          <Link to="/" style={{ color: 'var(--teal)' }} className="hover:underline">Volver a la herramienta</Link>
        </div>
      </div>
    );
  }

  const trimestre = (reporte.trimestre || '').replace(/\s/g, '');
  const anio = reporte.anio || reporte.año || '';
  const fechaTexto = reporte.fechaPublicacion
    ? new Date(reporte.fechaPublicacion).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="reporte-pinama">
      {/* En móvil: barra superior con volver */}
      <div className="lg:hidden sticky top-0 z-10 px-4 py-3" style={{ background: 'rgba(255,255,255,0.9)', borderBottom: '1px solid rgba(var(--color-ink), 0.1)' }}>
        <Link to="/" className="toc-link" style={{ display: 'inline-flex' }}>← Volver a la herramienta</Link>
      </div>

      {/* Hero con imagen de fondo (como referencia) */}
      <header className="hero hero-with-bg" id="hero">
        <img
          src="https://pinama23-modern.vercel.app/pinama/hero-bg.png"
          alt=""
          className="hero-bg-img"
          onError={(e) => {
            e.target.style.display = 'none';
            const hero = e.target.closest('.hero');
            if (hero) hero.classList.remove('hero-with-bg');
          }}
        />
        <div className="hero-overlay" aria-hidden="true" />
        <div className="container hero-inner">
          <img
            src="https://pinama23-modern.vercel.app/pinama/logo.png"
            alt="Pinama Capital 23"
            className="hero-logo"
            width={180}
            height={60}
          />
          <p className="hero-label">Informe Trimestral</p>
          <h1 className="hero-title">Actividad y Participadas {trimestre} {anio}</h1>
          <p className="hero-date">Madrid, {fechaTexto}</p>
        </div>
      </header>

      <div className="page-wrap">
        <aside className="sidebar-toc">
          <div className="sidebar-toc-title">Informe Trimestral</div>
          <nav className="toc-nav">
            <Link to="/" className="toc-link">← Volver</Link>
            {NAV_ITEMS.map((item) => (
              <a key={item.id} href={`#${item.id}`} className="toc-link">
                <TocIcon id={item.id} />
                {item.label}
              </a>
            ))}
          </nav>
        </aside>

        <main className="content-main">
          <VistaInforme
            reporte={reporte}
            contenidoGenerico={contenidoGenerico}
            startupsReporte={startupsReporte}
            showHeader={false}
          />
        </main>
      </div>
    </div>
  );
}
